<template>
    <div class="championship-sub-tabs">
        <div class="sub-tabs-switcher stage-tabs-grid">
            <button
                @click="activeSubTab = 'standings'"
                class="sub-switch-btn stage-area-regular"
                :class="{ active: activeSubTab === 'standings' }"
            >
                <span class="sub-switch-btn__label">Регулярный сезон</span>
                <span v-if="stagePeriods.standings" class="sub-switch-btn__period">{{ stagePeriods.standings }}</span>
            </button>

            <button
                @click="activeSubTab = 'quarterfinal'"
                class="sub-switch-btn stage-area-quarter"
                :class="{ active: activeSubTab === 'quarterfinal' }"
            >
                <span class="sub-switch-btn__label">Четвертьфиналы</span>
                <span v-if="stagePeriods.quarterfinal" class="sub-switch-btn__period">{{ stagePeriods.quarterfinal }}</span>
            </button>

            <button
                @click="activeSubTab = 'semifinal'"
                class="sub-switch-btn stage-area-semi"
                :class="{ active: activeSubTab === 'semifinal' }"
            >
                <span class="sub-switch-btn__label">Полуфиналы</span>
                <span v-if="stagePeriods.semifinal" class="sub-switch-btn__period">{{ stagePeriods.semifinal }}</span>
            </button>

            <button
                @click="activeSubTab = 'third_place'"
                class="sub-switch-btn sub-switch-btn--medal stage-area-third"
                :class="{ active: activeSubTab === 'third_place' }"
                aria-label="Матчи за 3 место"
            >
                <img
                    src="/images/icons/medal-bronze.svg"
                    alt=""
                    class="sub-switch-btn__medal"
                    aria-hidden="true"
                />
                <span v-if="stagePeriods.third_place" class="sub-switch-btn__period">{{ stagePeriods.third_place }}</span>
            </button>

            <button
                @click="activeSubTab = 'final'"
                class="sub-switch-btn sub-switch-btn--medal stage-area-final"
                :class="{ active: activeSubTab === 'final' }"
                aria-label="Матчи за 1 место"
            >
                <img
                    src="/images/icons/medal-gold.svg"
                    alt=""
                    class="sub-switch-btn__medal"
                    aria-hidden="true"
                />
                <span v-if="stagePeriods.final" class="sub-switch-btn__period">{{ stagePeriods.final }}</span>
            </button>

            <button
                @click="activeSubTab = '5-places'"
                class="sub-switch-btn stage-area-five"
                :class="{ active: activeSubTab === '5-places' }"
            >
                <span class="sub-switch-btn__label">Матчи за 5-9 места</span>
                <span v-if="stagePeriods['5-places']" class="sub-switch-btn__period">{{ stagePeriods['5-places'] }}</span>
            </button>
        </div>

        <div class="sub-tab-content">
            <!-- Регулярный сезон с переключением вида -->
            <div v-if="activeSubTab === 'standings'">
                <div class="view-switcher">
                    <button @click="standingsView = 'table'" class="view-btn"
                        :class="{ active: standingsView === 'table' }">
                        Таблица
                    </button>
                    <button @click="standingsView = 'matches'" class="view-btn"
                        :class="{ active: standingsView === 'matches' }">
                        Матчи
                    </button>
                </div>

                <standings-tab v-if="standingsView === 'table'" :standings="standings" />
                <championship-tab v-else :teams="teams" :regular-games="regularGames" :playoff-games="[]" />
            </div>

            <!-- Четвертьфиналы -->
            <championship-stage v-if="activeSubTab === 'quarterfinal'" :games="getStageGames('quarterfinal')"
                stage="quarterfinal" :stage-name="getStageName('quarterfinal')" />

            <!-- Матчи за 5-9 места -->
            <div v-if="activeSubTab === '5-places'">
                <div class="view-switcher">
                    <button @click="fivePlacesView = 'table'" class="view-btn"
                        :class="{ active: fivePlacesView === 'table' }">
                        Таблица
                    </button>
                    <button @click="fivePlacesView = 'matches'" class="view-btn"
                        :class="{ active: fivePlacesView === 'matches' }">
                        Матчи
                    </button>
                </div>

                <standings-tab v-if="fivePlacesView === 'table'" :standings="fivePlaces?.standings || []" />
                <championship-tab v-else :teams="fivePlaces?.teams || []" :regular-games="fivePlaces?.games || []"
                    :playoff-games="[]" :max-matches-per-pair="4" :position-offset="4" />
            </div>

            <!-- Полуфиналы -->
            <championship-stage v-if="activeSubTab === 'semifinal'" :games="getStageGames('semifinal')"
                stage="semifinal" :stage-name="getStageName('semifinal')" />

            <!-- Матчи за 3 место -->
            <championship-stage v-if="activeSubTab === 'third_place'" :games="getStageGames('third_place')"
                stage="third_place" :stage-name="getStageName('third_place')" />

            <!-- Матчи за 1 место (Финал) -->
            <championship-stage v-if="activeSubTab === 'final'" :games="getStageGames('final')" stage="final"
                :stage-name="getStageName('final')" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import StandingsTab from '@/components/features/results/StandingsTab.vue';
import ChampionshipTab from '@/components/features/results/ChampionshipTab.vue';
import ChampionshipStage from '@/components/features/results/ChampionshipStage.vue';
import { formatGamesPeriod } from '@/utils/gameDateRange';

const props = defineProps({
    standings: {
        type: Array,
        required: true,
        default: () => []
    },
    teams: {
        type: Array,
        required: true,
        default: () => []
    },
    regularGames: {
        type: Array,
        default: () => []
    },
    playoffGames: {
        type: [Array, Object],
        default: () => []
    },
    playoffGamesByStage: {
        type: Object,
        default: () => ({})
    },
    fivePlaces: {
        type: Object,
        default: () => ({})
    }
});

const activeSubTab = ref('standings');
const standingsView = ref('table'); // 'table' или 'matches'
const fivePlacesView = ref('table');

const getStageGames = (stage) => {
    if (props.playoffGamesByStage && props.playoffGamesByStage[stage]) {
        return props.playoffGamesByStage[stage].games || [];
    }
    if (Array.isArray(props.playoffGames)) {
        const stageGroup = props.playoffGames.find(g => g.stage === stage);
        return stageGroup ? stageGroup.games : [];
    }
    return [];
};

const getStageName = (stage) => {
    const stageNames = {
        'quarterfinal': 'Четвертьфиналы',
        '5-places': 'Матчи за 5-9 места',
        'semifinal': 'Полуфиналы',
        'third_place': 'Матчи за 3 место',
        'final': 'Матчи за 1 место'
    };
    return stageNames[stage] || stage;
};

const stagePeriods = computed(() => ({
    standings: formatGamesPeriod(props.regularGames),
    quarterfinal: formatGamesPeriod(getStageGames('quarterfinal')),
    semifinal: formatGamesPeriod(getStageGames('semifinal')),
    third_place: formatGamesPeriod(getStageGames('third_place')),
    final: formatGamesPeriod(getStageGames('final')),
    '5-places': formatGamesPeriod(getStageGames('5-places')),
}));
</script>
